import Fuse from "fuse.js";
interface SearchResult {
	index: number;
	match: string;
	score: number;
	method: "exact" | "fuzzy";
}

/**
 * @param {number} numToFind - Contains the number which was found between two numbers of our hashmap
 * @param {object} map - Contains the map of indexes which contains the metadata regaring that index
 */
const findPartialStringIndex = (numToFind, map) => {
	const hashMapValues = Object.keys(map);
	let low = 0;
	let high = hashMapValues.length - 1;
	let resultIndex = -1; // Initialize to -1 to indicate "not found" if no larger number exists

	while (low <= high) {
		const mid = Math.floor((low + high) / 2);
		if (hashMapValues[mid] > numToFind) {
			resultIndex = mid; // Update resultIndex with the current mid index
			high = mid - 1; // Continue searching in the lower half to find a closer larger number
		} else {
			low = mid + 1; // Look in the right half
		}
	}
	//  resultIndex means the nearest largest number and -1 means a number lower than that which will fullfill
	// our original request to find the origin of the partial string, because lets say the number we got is 13 and the two elements in between which its lie is 9 and 15
	//  then obviously the partial string belongs to element 9 because next element is just starting from 15
	if (resultIndex === 0) {
		return hashMapValues[resultIndex];
	}

	return hashMapValues[resultIndex - 1];
};

/**
 * @param {string} pattern - Contains the string user searched for
 */
function buildBadCharTable(pattern) {
	const table = {};
	for (let i = 0; i < pattern.length; i++) {
		table[pattern[i]] = i;
	}
	return table;
}

/**
 * @param {string} pattern - Contains the string user searched for
 */
function buildGoodSuffixTable(pattern) {
	const len = pattern.length;
	const suffixShift = new Array(len).fill(0);
	const borderPosition = new Array(len + 1).fill(-1);

	let i = len;
	let j = len + 1;
	borderPosition[i] = j;
	while (i > 0) {
		while (j <= len && pattern[i - 1] !== pattern[j - 1]) {
			if (suffixShift[j - 1] === 0) suffixShift[j - 1] = j - i;
			j = borderPosition[j];
		}
		i--;
		j--;
		borderPosition[i] = j;
	}

	j = borderPosition[0];
	for (i = 0; i <= len; i++) {
		if (suffixShift[i] === 0) suffixShift[i] = j;
		if (i === j) j = borderPosition[j];
	}

	return suffixShift;
}

// export const normalizeText = (str: string) => {
// 	return str
// 		.toLowerCase()
// 		.replace(/\s+/g, "") // remove spaces if any
// 		.replace(/\d+/g, ""); // remove all digits
// };

/**
 * @param {string} pattern - Contains the string user searched for
 * @param {string} text - Contains a long concatenated string made up of every answer shown in the current selected category
 */
function searchText(text, pattern) {
	const badChar = buildBadCharTable(pattern);
	const goodSuffix = buildGoodSuffixTable(pattern);
	const results = [];
	let s = 0; // s is the shift of the pattern with respect to text

	while (s <= text.length - pattern.length) {
		let j = pattern.length - 1;
		while (j >= 0 && pattern[j] === text[s + j]) {
			j--;
		}
		if (j < 0) {
			results.push(s);
			s += goodSuffix[0];
		} else {
			const badCharShift = badChar[text[s + j]] !== undefined ? j - badChar[text[s + j]] : j + 1;
			const goodSuffixShift = goodSuffix[j];
			s += Math.max(badCharShift, goodSuffixShift);
		}
	}
	return results;
}

/**
 * Retrieves the closest matching index object from the given `textMap` based on the input `numToCheck`.
 *
 * The function attempts to locate the exact key `numToCheck` in `textMap.index_map`. If found,
 * it returns the corresponding object and sets `currentIndex` to `numToCheck`.
 *
 * If the exact key is not present:
 * - If `numToCheck` is greater than or equal to `textMap.lastIndex`, the value at `lastIndex` is returned.
 * - Otherwise, a binary search is performed on the keys of `index_map` to find the **lower bound** —
 *   the greatest key less than or equal to `numToCheck`. The associated object is then returned,
 *   with `currentIndex` set to that lower-bound key.
 */
const findPartialStringIndexWrapper = (numToCheck, textMap) => {
	let finalIndex;
	if (textMap.index_map[numToCheck] !== undefined) {
		finalIndex = textMap.index_map[numToCheck];
		finalIndex.currentIndex = numToCheck;
	} else if (numToCheck >= textMap.lastIndex) {
		finalIndex = textMap.index_map[textMap.lastIndex];
		finalIndex.currentIndex = textMap.lastIndex;
	} else {
		const partialIndex = findPartialStringIndex(numToCheck, textMap.index_map);
		finalIndex = textMap.index_map[partialIndex];
		finalIndex.currentIndex = partialIndex;
	}
	return finalIndex;
};

// Cant use the normal .include function because it cant find half string and my starting string
// would contain only a portion of the whole string
// * Finds string pattern matches using the Knuth-Morris-Pratt algorithm while ignoring spaces.
// We are ignoring spaces here because when we compare with spaces it creates problems because both strings doesnot
//  match with respect to spaces
//  *
//  * This function can find either:
//  * 1. The longest suffix of string A that matches a prefix of string B, or
//  * 2. The longest prefix of string A that matches a suffix of string B
//  *
//  * It uses KMP's prefix function (π table) on a combined string to efficiently
//  * identify the matching patterns between the two strings.
//  *    - 'suffixPrefix': Find longest suffix of A that matches prefix of B
//  *   - 'prefixSuffix': Find longest prefix of A that matches suffix of B

// Find where suffix of "ab cd ef" matches prefix of "ef gh i"
// * findStringMatch({string: "ab cd ef"}, "ef gh i", "suffixPrefix"); // Returns 6
// *
// * // Find length of prefix of "ab c def" that matches suffix of "xy za bc"
// * findStringMatch({string: "ab c def"}, "xy za bc", "prefixSuffix"); // Returns 4

// **************** Important Case****************
// Incase i get a direct match then i return { fullMatch: true, index: A.indexOf(B) }

function findStringMatchKMP(stringA, stringB, matchType = "suffixPrefix") {
	const originalA = stringA;
	const originalB = stringB;

	// Remove spaces from both strings
	const A = originalA;
	const B = originalB;

	if (A.includes(B)) {
		return { fullMatch: true, index: A.indexOf(B) };
	}

	// If either string is empty after removing spaces, no match is possible
	if (A.length === 0 || B.length === 0) {
		return -1;
	}

	// Create mapping from no-space index to original index for string A
	const indexMapA = [];
	let noSpaceIndex = 0;
	for (let i = 0; i < originalA.length; i++) {
		if (originalA[i] !== " ") {
			indexMapA[noSpaceIndex] = i;
			noSpaceIndex++;
		}
	}

	// Create reverse mapping to find length in original string
	const getOriginalLength = (start, length) => {
		if (start < 0 || start >= A.length) return 0;
		if (length <= 0) return 0;

		const endIndex = Math.min(start + length - 1, A.length - 1);
		const originalStart = indexMapA[start];
		const originalEnd = indexMapA[endIndex];
		return originalEnd - originalStart + 1;
	};

	let combined;
	if (matchType === "suffixPrefix") {
		// Original case: B + '#' + A to find suffix of A matching prefix of B
		combined = B + "#" + A;
	} else if (matchType === "prefixSuffix") {
		// New case: A + '#' + B to find prefix of A matching suffix of B
		combined = A + "#" + B;
	} else {
		throw new Error('Invalid matchType. Use "suffixPrefix" or "prefixSuffix"');
	}

	// Build the KMP prefix function (π table)
	const pi = Array(combined.length).fill(0);
	for (let i = 1; i < combined.length; i++) {
		let j = pi[i - 1];
		while (j > 0 && combined[i] !== combined[j]) {
			j = pi[j - 1];
		}
		if (combined[i] === combined[j]) {
			j++;
		}
		pi[i] = j;
	}

	const matchLen = pi[pi.length - 1];

	// If matchLen is 0, no match was found
	if (matchLen === 0) {
		return -1;
	}

	if (matchType === "suffixPrefix") {
		// Calculate starting index in the space-removed string A
		const startIndexNoSpace = A.length - matchLen;

		// Convert to original index if valid
		if (startIndexNoSpace >= 0 && startIndexNoSpace < A.length) {
			return indexMapA[startIndexNoSpace];
		}
		return -1;
	} else {
		// For prefixSuffix, we return original length of the matching prefix
		return getOriginalLength(0, matchLen);
	}
}

export { searchText, findPartialStringIndex, findPartialStringIndexWrapper, findStringMatchKMP };
